import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { rerouteService } from '../../services';
import { GiCommercialAirplane } from 'react-icons/gi';

const RerouteLab = () => {
  const navigate = useNavigate();
  const [rotationNumber, setRotationNumber] = useState('');
  const [rules, setRules] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [detectLoading, setDetectLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [detectResult, setDetectResult] = useState(null);
  const [payResult, setPayResult] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const breadcrumbItems = [
    { label: 'Reroute Lab', icon: 'GitBranch' }
  ];

  const normalizeList = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.items)) return payload.items;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.rules)) return payload.rules;
    if (Array.isArray(payload?.scenarios)) return payload.scenarios;
    return [];
  };

  const loadRules = async () => {
    setRulesLoading(true);
    setError(null);
    try {
      const data = await rerouteService.getRules();
      setRules(normalizeList(data));
    } catch (e) {
      setError(e.message || 'Failed to load reroute rules');
    } finally {
      setRulesLoading(false);
    }
  };

  const loadScenarios = async () => {
    setScenariosLoading(true);
    setError(null);
    try {
      const data = await rerouteService.getScenarios();
      setScenarios(normalizeList(data));
    } catch (e) {
      setError(e.message || 'Failed to load reroute scenarios');
    } finally {
      setScenariosLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
    loadScenarios();
  }, []);

  const handleDetect = async () => {
    const trimmed = rotationNumber.trim();
    if (!trimmed) {
      setError('Enter a rotation number (e.g., 4302 or C401)');
      return;
    }
    setDetectLoading(true);
    setError(null);
    setInfo(null);
    try {
      const data = await rerouteService.detectReroute(trimmed);
      setDetectResult(data);
      setInfo('Detection complete. Using rotation_number (business id) per Golden Rule.');
    } catch (e) {
      setError(e.message || 'Failed to detect reroute');
    } finally {
      setDetectLoading(false);
    }
  };

  const handleCalculate = async () => {
    const trimmed = rotationNumber.trim();
    if (!trimmed) {
      setError('Enter a rotation number (e.g., 4302 or C401)');
      return;
    }
    setPayLoading(true);
    setError(null);
    setInfo(null);
    try {
      const data = await rerouteService.calculate23lPay(trimmed);
      setPayResult(data);
      setInfo('Path A: pay is hours-only. Multiply credit/pay hours by your hourly_rate for dollars.');
    } catch (e) {
      setError(e.message || 'Failed to calculate §23L pay');
    } finally {
      setPayLoading(false);
    }
  };

  const renderJson = (data) => (
    <pre className="mt-2 rounded-lg bg-slate-900 text-slate-100 text-xs p-3 overflow-auto max-h-80">
      {JSON.stringify(data, null, 2)}
    </pre>
  );

  const renderRuleCard = (rule) => (
    <div key={rule.rule_id || rule.id || rule.title} className="border border-slate-200 rounded-lg p-3 bg-white shadow-sm">
      <div className="font-semibold text-slate-900">{rule.rule_id || rule.title || 'Rule'}</div>
      <div className="text-sm text-slate-600">{rule.pwa_section || rule.section || 'Section N/A'}</div>
      {rule.title && <div className="text-sm text-slate-700 mt-1">{rule.title}</div>}
      {rule.description && <div className="text-xs text-slate-500 mt-1 leading-relaxed">{rule.description}</div>}
    </div>
  );

  const renderScenarioCard = (scenario) => (
    <div key={scenario.scenario_id || scenario.id || scenario.scenario_name} className="border border-indigo-100 rounded-lg p-3 bg-indigo-50">
      <div className="font-semibold text-slate-900">{scenario.scenario_name || scenario.name || 'Scenario'}</div>
      <div className="text-sm text-indigo-700">Severity: {scenario.severity || 'info'}</div>
      {scenario.scenario_type && <div className="text-sm text-slate-700">Type: {scenario.scenario_type}</div>}
      {scenario.description && <div className="text-xs text-slate-600 mt-1 leading-relaxed">{scenario.description}</div>}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <main className="main-content">
        <div className="content-wrapper space-y-6">
        <Breadcrumb items={breadcrumbItems} />

        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-slate-900">Reroute Lab</h1>
          <p className="text-sm text-slate-600">
            Use rotation_number (business identifier). Path A returns hours-only for pay; multiply by your hourly_rate to get dollars.
          </p>
        </div>

        {/* ✅ NEW: Navigate to Reroute Simulator */}
        <button
          onClick={() => navigate('/reroute-simulator')}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white px-4 py-3 text-sm font-semibold hover:bg-emerald-700 shadow-md hover:shadow-lg transition-all duration-200"
          style={{ maxWidth: '280px' }}
        >
          <GiCommercialAirplane className="text-lg" />
          <span>Open Reroute Simulator</span>
        </button>

        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 p-3 text-sm">
            {error}
          </div>
        )}
        {info && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 p-3 text-sm">
            {info}
          </div>
        )}

        <section className="grid gap-4 lg:grid-cols-3">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-4">
            <div>
              <div className="text-sm font-semibold text-slate-900">Run detection & §23L calc</div>
              <p className="text-xs text-slate-600">Example: 4302 or C401. No database id needed.</p>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-700">Rotation Number</label>
              <input
                type="text"
                value={rotationNumber}
                onChange={(e) => setRotationNumber(e.target.value)}
                placeholder="Enter rotation_number (e.g., 4302)"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={handleDetect}
                disabled={detectLoading}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-3 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
              >
                {detectLoading ? 'Detecting…' : 'Detect Reroute'}
              </button>
              <button
                onClick={handleCalculate}
                disabled={payLoading}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-3 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
              >
                {payLoading ? 'Calculating…' : 'Calculate §23L (hours)'}
              </button>
            </div>
            <div className="text-xs text-slate-600 leading-relaxed">
              For dollars: total_pay_hours × hourly_rate (frontend) per Path A. Backend does not return total_pay.
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-900">Detection Result</div>
              {detectLoading && <span className="text-xs text-slate-500">Loading…</span>}
            </div>
            {detectResult ? renderJson(detectResult) : (
              <p className="text-xs text-slate-600 mt-2">Run detection to see reroute classification and FAR 117 notes.</p>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-900">§23L Pay (hours-only)</div>
              {payLoading && <span className="text-xs text-slate-500">Loading…</span>}
            </div>
            <p className="text-xs text-slate-600 mt-1">Multiply returned hours by your hourly_rate to display dollars.</p>
            {payResult ? renderJson(payResult) : (
              <p className="text-xs text-slate-600 mt-2">Run the §23L calculation to view credit/pay hours.</p>
            )}
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-2">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-900">PWA Rules</div>
              {rulesLoading && <span className="text-xs text-slate-500">Loading…</span>}
            </div>
            {rules.length === 0 && !rulesLoading && (
              <p className="text-xs text-slate-600">No rules returned.</p>
            )}
            <div className="grid gap-2 md:grid-cols-2">
              {rules.slice(0, 10).map(renderRuleCard)}
            </div>
            {rules.length > 10 && (
              <div className="text-xs text-slate-500">Showing first 10 of {rules.length} rules.</div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-900">Reroute Scenarios</div>
              {scenariosLoading && <span className="text-xs text-slate-500">Loading…</span>}
            </div>
            {scenarios.length === 0 && !scenariosLoading && (
              <p className="text-xs text-slate-600">No scenarios returned.</p>
            )}
            <div className="grid gap-2 md:grid-cols-2">
              {scenarios.slice(0, 10).map(renderScenarioCard)}
            </div>
            {scenarios.length > 10 && (
              <div className="text-xs text-slate-500">Showing first 10 of {scenarios.length} scenarios.</div>
            )}
          </div>
        </section>
        </div>
      </main>
    </div>
  );
};

export default RerouteLab;
